import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getSalesReportaData, formatDateForApi } from '../../utilities/api';

/**
 * Sales Reporta: closed bills only, read straight from product_billdetail, pivoted by
 * final_mop with total_paid_amount as the amount. Response shape is
 * { bills: [{ invoice_date, <mode>: amount, ..., total }], summary: { <mode>: amount, ..., total } } —
 * flatter than Sales Reports (no NC/Credit/Cancelled buckets, since non-closed bills
 * are excluded server-side), so no per-row shaping is needed beyond renaming `total`.
 */
const shapeRows = (bills) =>
    (Array.isArray(bills) ? bills : []).map((row) => {
        const { total, ...rest } = row;
        return { ...rest, Total: total ?? 0 };
    });

const shapeSummary = (summary) => {
    const flat = {};
    Object.entries(summary || {}).forEach(([key, value]) => {
        const label = key === 'total' ? 'Total_Total' : `Total_${key}`;
        flat[label] = value;
    });
    return flat;
};

export const fetchSalesReportaData = createAsyncThunk(
    'salesReporta/fetchSalesReportaData',
    async ({ fromDate, toDate, branchId }, { rejectWithValue }) => {
        try {
            const resolvedBranchId = branchId || 'All';
            const clientId = localStorage.getItem('clientid') || '';
            const rolename = localStorage.getItem('role') || '';
            const payload = {
                from_date: fromDate ? formatDateForApi(fromDate) : '',
                to_date: toDate ? formatDateForApi(toDate) : '',
                branch_id: resolvedBranchId,
                rolename,
                clientname: clientId,
                client_id: clientId
            };
            const data = await getSalesReportaData(payload);
            if (data?.error) {
                return rejectWithValue(data.error);
            }
            return data;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch sales reporta data');
        }
    }
);

const initialState = {
    selectedBranch: null,
    fromDate: null,
    toDate: null,

    salesData: [],
    summary: {},
    paymentModes: [],

    loading: false,
    error: null
};

const salesReportaSlice = createSlice({
    name: 'salesReporta',
    initialState,
    reducers: {
        setFromDate: (state, action) => {
            state.fromDate = action.payload;
        },
        setToDate: (state, action) => {
            state.toDate = action.payload;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
        },
        resetFilters: (state) => {
            state.fromDate = null;
            state.toDate = null;
            state.selectedBranch = null;
            state.salesData = [];
            state.summary = {};
            state.paymentModes = [];
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSalesReportaData.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.salesData = [];
            })
            .addCase(fetchSalesReportaData.fulfilled, (state, action) => {
                state.loading = false;
                const bills = action.payload?.bills || [];
                state.salesData = shapeRows(bills);
                state.summary = shapeSummary(action.payload?.summary || {});
                const modeSet = new Set();
                bills.forEach((row) => {
                    Object.keys(row).forEach((k) => {
                        if (k === 'invoice_date' || k === 'total') return;
                        modeSet.add(k);
                    });
                });
                state.paymentModes = Array.from(modeSet);
            })
            .addCase(fetchSalesReportaData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.salesData = [];
                state.summary = {};
                state.paymentModes = [];
            });
    }
});

export const { setFromDate, setToDate, setSelectedBranch, resetFilters, clearError } = salesReportaSlice.actions;

export const selectSalesData = (state) => state.salesReporta.salesData;
export const selectSummary = (state) => state.salesReporta.summary;
export const selectPaymentModes = (state) => state.salesReporta.paymentModes;
export const selectLoading = (state) => state.salesReporta.loading;
export const selectSelectedBranch = (state) => state.salesReporta.selectedBranch;
export const selectFromDate = (state) => state.salesReporta.fromDate;
export const selectToDate = (state) => state.salesReporta.toDate;
export const selectError = (state) => state.salesReporta.error;

export default salesReportaSlice.reducer;
